<template>
  <div class="login-form">
    <div class="login-header">
      <h1 class="login-title" :style="getPrimaryColor">
        <i class="ph ph-user-switch"></i>
        {{ getTenantName }} Login
      </h1>
      <div class="nav-link text-right" v-if="isTenantUserRegistrationEnabled">
        Don't have an account ?
        <router-link to="/ap/reg" class="fw-bold" :style="getPrimaryColor">SignUp!</router-link>
      </div>
    </div>

    <div class="divider">
      <span class="line"></span>
      <span class="line"></span>
    </div>

    <!-- DEFAULT: email verification step -->
    <el-form
      v-if="currentForm === 'default'"
      class="log-form"
      :model="verification"
      ref="verification"
      @submit.native.prevent="verifyEmail('verification')"
    >
      <el-form-item prop="email">
        User name
        <span class="text-danger">*</span>
        <el-input
          v-model="verification.email"
          placeholder="Email or Mobile"
          autocomplete="off"
        ></el-input>
        <p class="error" v-if="getErrors && getErrors.verification_email">
          {{ getErrors.verification_email }}
        </p>
        <p class="form-help" v-if="zeroCompaniesNote">
          {{ zeroCompaniesNote }}
        </p>
      </el-form-item>

      <el-form-item class="mt-1">
        <el-button
          type="primary"
          size="medium"
          round
          native-type="button"
          class="login-button full-width"
          @click="verifyEmail('verification')"
          :loading="submitLoading"
          :style="getButtonStyles"
        >
          Click here to verify
        </el-button>
      </el-form-item>

      <p class="error" v-if="getCompaniesByEmail && !getCompaniesByEmail.status">
        {{ getCompaniesByEmail.message }}
      </p>

      <div class="divider">
        <span class="line"></span>
        <span class="divider-text">OR</span>
        <span class="line"></span>
      </div>

      <div class="forgot-login-row">
        <a @click="goToBuilder" class="forgot-password-link" :style="getPrimaryColor">
          Start building your app — log in to the Builder
        </a>
      </div>
    </el-form>

    <!-- VERIFIED -->
    <el-form
      v-if="currentForm === 'VERIFIED'"
      :model="signin"
      ref="signin"
      class="log-form"
      @submit.native.prevent="submitForm('signin')"
    >
      <h3>Welcome {{ getLoginUserName }}!</h3>

      <p class="form-help" v-if="fromMagicLink">
        You opened a secure link from your email. We’ve pre-filled your email and OTP.
      </p>

      <el-form-item prop="email">
        User name
        <span class="text-danger">*</span>
        <el-input
          v-model="signin.email"
          placeholder="Email or Mobile"
          disabled
          autocomplete="off"
        ></el-input>
      </el-form-item>

      <el-form-item class="mb-1" prop="workspace">
        Workspace
        <span class="text-danger">*</span>
        <el-select
          v-model="signin.workspace"
          filterable
          placeholder="Select"
          style="width: 100%"
          :disabled="fromMagicLink && !!signin.workspace"
        >
          <el-option
            v-for="item in getWorkspaceList"
            :key="item.contact_id"
            :label="item.name"
            :value="item.contact_id"
          ></el-option>
        </el-select>
        <p class="error" v-if="getErrors && getErrors.workspace">
          {{ getErrors.workspace }}
        </p>
      </el-form-item>

      <!-- Password flow -->
      <el-form-item class="mb-1" prop="password" v-if="selectedCompany?.password === true">
        Password
        <span class="text-danger">*</span>
        <div class="password-input-wrapper">
          <el-input
            :type="passwordType"
            placeholder="Enter Password"
            v-model="signin.password"
            autocomplete="off"
          ></el-input>
          <p class="error" v-if="getErrors && getErrors.password">
            {{ getErrors.password }}
          </p>
          <span
            class="password-toggle"
            @click="passwordType = passwordType === 'password' ? 'text' : 'password'"
          >
            <i v-if="passwordType === 'password'" class="ph ph-eye"></i>
            <i v-if="passwordType === 'text'" class="ph ph-eye-slash"></i>
          </span>
        </div>
      </el-form-item>

      <!-- OTP + Set Password flow -->
      <el-form-item class="mb-1" v-if="selectedCompany?.password === false">
        <el-form
          ref="setPasswordForm"
          :model="{ otp, setPassword, conformPassword }"
          class="log-form"
        >
          <p class="form-section-subtext">
            It looks like you haven't set a password yet. Please set a password to secure your
            account
          </p>

          <el-form-item label="Enter OTP" prop="otp" class="form-item-top-label">
            <el-input v-model="otp" placeholder="Enter OTP" autocomplete="off" />
          </el-form-item>

          <el-form-item
            label="Set Password"
            prop="setPassword"
            class="form-item-top-label"
            :error="passwordValidationError"
          >
            <el-input
              v-model="setPassword"
              :type="passwordType"
              placeholder="Set Password"
              autocomplete="off"
              class="custom-password-input"
            >
              <template #suffix>
                <i
                  :class="passwordType === 'password' ? 'ph ph-eye' : 'ph ph-eye-slash'"
                  class="toggle-icon"
                  @click="passwordType = passwordType === 'password' ? 'text' : 'password'"
                ></i>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item
            label="Confirm Password"
            prop="conformPassword"
            class="form-item-top-label"
            :error="confirmPasswordValidationError"
          >
            <el-input
              v-model="conformPassword"
              :type="confirmPasswordType"
              placeholder="Confirm Password"
              autocomplete="off"
              class="custom-password-input"
            >
              <template #suffix>
                <i
                  :class="confirmPasswordType === 'password' ? 'ph ph-eye' : 'ph ph-eye-slash'"
                  class="toggle-icon"
                  @click="
                    confirmPasswordType = confirmPasswordType === 'password' ? 'text' : 'password'
                  "
                ></i>
              </template>
            </el-input>
          </el-form-item>

          <el-form-item>
            <el-button
              class="full-width"
              type="primary"
              :style="getButtonStyles"
              :disabled="!otp || !!passwordValidationError || !!confirmPasswordValidationError"
              @click="checkPassword"
            >
              Confirm
            </el-button>
          </el-form-item>

          <div class="nav-link d-flex justify-content-between align-items-center mt-2">
            <el-link class="no-underline" :underline="false" type="info" @click="refreshToSignin">
              Sign in with another account ?
            </el-link>

            <el-link
              v-if="isEmailOpted"
              @click="resendVerificationCode(null)"
              type="primary"
              class="no-underline"
              :underline="false"
            >
              Resend OTP
            </el-link>
            <el-link
              v-else
              @click="resendVerificationCode(null)"
              type="primary"
              class="no-underline"
              :underline="false"
            >
              Resend OTP
            </el-link>
          </div>
        </el-form>
      </el-form-item>

      <el-form-item class="mt-1" v-if="selectedCompany?.password === true">
        <div class="forgot-login-row space-between">
          <div class="nav-link text-right forgot-password-link" @click="goToForgotPassword">
            Forgot Password?
          </div>
          <el-button
            type="primary"
            size="medium"
            round
            native-type="submit"
            :loading="submitLoading"
            :style="getButtonStyles"
          >
            LogIn
          </el-button>
        </div>
      </el-form-item>

      <p class="error" v-if="getCompaniesByEmail && !getCompaniesByEmail.status">
        {{ getCompaniesByEmail.message }}
      </p>
    </el-form>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import UserHelper from '@/mixins/UserHelper';
  import WhiteLabelConfig from '@/config/app';
  import CustomizationLayer from '@/mixins/CustomizationLayer';
  import CryptoJS from 'crypto-js';
  import axios from 'axios';
  import config from '@/config/app';

  const ENC_KEY = config.URL_ENC_KEY;
  const API_BASE = process.env.VUE_APP_API_BASE || process.env.API_BASE || '';

  export default {
    data() {
      return {
        password_rules: [
          {
            message: 'Must include uppercase, lowercase, number, and special character',
            regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$@!%&*?])[A-Za-z\d#$@!%&*?]{8,30}$/,
          },
        ],
        isEmailOpted: true,
        passwordErrors: [],
        confirmPasswordType: 'password',
        otp: '',
        setPassword: '',
        conformPassword: '',
        successMessages: '',
        passwordError: '',
        currentForm: 'default',
        verification: { email: '' },
        signin: { email: '', password: '', workspace: '' },
        passwordType: 'password',
        submitLoading: false,
        getErrors: null,
        selectedCompany: null,
        application_config: {},
        contactDetails: { email: '', phone: '' },
        zeroCompaniesNote: '',
        fromMagicLink: false,
        hasAutoResentFor: new Set(),
      };
    },
    mixins: [UserHelper, CustomizationLayer],
    computed: {
      passwordValidationError() {
        if (!this.setPassword) return '';
        const rule = this.password_rules.find((r) => !r.regex.test(this.setPassword));
        return rule ? rule.message : '';
      },
      confirmPasswordValidationError() {
        if (!this.conformPassword) return '';
        if (this.setPassword !== this.conformPassword) return 'Passwords do not match.';
        return '';
      },
      ...mapGetters('applicationUsers', [
        'getUserVerifyDetails',
        'getUserEmailVerifyDetails',
        'getUserEmailVerificationErrors',
        'getUserSignInVerify',
        'getForgotAndResendVerify',
        'getForgotAndResendVerifyError',
      ]),
      ...mapGetters('company', ['getCompaniesByEmail', 'getContactLoginSlug']),
      ...mapGetters('auth', ['getAuthenticationStatus', 'getUserSignInErrors']),
      getLoginUserName() {
        const companies = this.getCompaniesByEmail || [];
        return companies?.status && companies.companies?.length
          ? `${companies.companies[0].first_name} ${companies.companies[0].last_name}`
          : '';
      },
      getEmail() {
        const companies = this.getCompaniesByEmail || [];
        return companies?.status && companies.companies?.length ? companies.companies[0].email : '';
      },
      getWorkspaceList() {
        const companies = this.getCompaniesByEmail || [];
        return companies?.status && companies.companies?.length ? companies.companies : [];
      },
      isEmailValid() {
        const email = this.verification.email?.trim().toLowerCase();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
      },
    },
    watch: {
      getWorkspaceList(val) {
        if (Array.isArray(val) && val.length === 1) {
          this.signin.workspace = val[0].contact_id;
          this.selectedCompany = val[0];
          if (this.selectedCompany.password === false) {
            this.setContactDetailsFromCompany(this.selectedCompany);
          }
        }
      },
      'signin.workspace'(val) {
        this.getErrors = null;
        this.passwordErrors = [];
        const match = this.getWorkspaceList.find((c) => c.contact_id === val);
        this.selectedCompany = match || null;

        if (this.selectedCompany && this.selectedCompany.password === false) {
          this.setContactDetailsFromCompany(this.selectedCompany);
          if (!this.fromMagicLink) {
            if (!this.hasAutoResentFor.has(val)) {
              this.hasAutoResentFor.add(val);
              this.resendVerificationCode();
            }
          }
        }
      },
    },
    mounted() {
      this.$store.commit('company/resetCompaniesByEmail');
      this.application_config = WhiteLabelConfig;

      // Clear any residual theme from previous login session
      this.$store.dispatch('theme/resetTheme');

      const q = this.$route?.query || {};
      if (q.t || q.token || q.c || q.e || q.v || q.company || q.email || q.vc) {
        this._bootstrapFromUrl(q);
      }
    },
    methods: {
      refreshToSignin() {
        window.location.reload();
      },
      changeViewInput() {
        this.isEmailOpted = !this.isEmailOpted;
      },

      async _postContactLoginCompany(payload, meta = {}) {
        const http = this.$axios || axios;
        const body = {
          company_slug: payload.company || '',
          email: payload.email || '',
          vc: payload.vc || '',
          is_encrypted: !!meta.is_encrypted,
          token_kind: meta.token_kind || '',
          raw_token: meta.raw_token || '',
          arrived_at: new Date().toISOString(),
          source: 'TENANT_LOGIN',
        };
        try {
          await http.post(`${API_BASE}/contact-login/company`, body);
        } catch (err) {
          console.warn('POST /contact-login/company failed:', err?.response?.data || err?.message);
        }
      },

      _base64UrlToStd(b64u = '') {
        return (b64u || '').replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/');
      },
      _decryptAES(cipherText) {
        try {
          if (!cipherText) return '';
          if (!ENC_KEY) return '';
          const normalized = this._base64UrlToStd(decodeURIComponent(cipherText));
          const bytes = CryptoJS.AES.decrypt(normalized, ENC_KEY);
          const out = bytes.toString(CryptoJS.enc.Utf8);
          return out || '';
        } catch (err) {
          return '';
        }
      },
      _looksEncrypted(v) {
        if (!v || typeof v !== 'string') return false;
        const s = v.trim();
        if (s.length < 12) return false;
        if (/\s/.test(s)) return false;
        return /^[A-Za-z0-9+/_-]+=*$/.test(s);
      },
      _maybeDecrypt(v) {
        if (!v) return '';

        if (this._looksEncrypted(v)) {
          const dec = this._decryptAES(v);
          if (dec) return dec;
        }
        return v;
      },
      decryptFromUrl(token) {
        return this._decryptAES(token);
      },

      async _bootstrapFromUrl(params) {
        let dec = null;
        if (params.t || params.token) {
          const raw = params.t || params.token;
          const plain = this.decryptFromUrl(raw);
          if (plain) {
            try {
              dec = JSON.parse(plain);
            } catch (err) {
              dec = null;
            }
          }
        }

        let company = '';
        let email = '';
        let vc = '';
        let tokenKind = 'plain';
        let isEncrypted = false;
        let rawToken = '';

        if (dec && typeof dec === 'object') {
          company = (dec.company || dec.c || '').toString();
          email = (dec.email || dec.e || '').toString();
          vc = (dec.vc || dec.v || '').toString();
          tokenKind = 'single-token';
          isEncrypted = true;
          rawToken = params.t || params.token || '';
        } else if (params.c || params.e || params.v) {
          company = this.decryptFromUrl(params.c || '') || '';
          email = this.decryptFromUrl(params.e || '') || '';
          vc = this.decryptFromUrl(params.v || '') || '';
          tokenKind = 'per-field';
          isEncrypted = true;
        } else {
          const rawCompany = this._safeDecode(params.company || '');
          const rawEmail = this._safeDecode(params.email || '');
          const rawVc = this._safeDecode(params.vc || '');

          company = this._maybeDecrypt(rawCompany);
          email = this._maybeDecrypt(rawEmail);
          vc = this._maybeDecrypt(rawVc);

          tokenKind =
            this._looksEncrypted(rawCompany) ||
            this._looksEncrypted(rawEmail) ||
            this._looksEncrypted(rawVc)
              ? 'plain-but-encrypted'
              : 'plain';
          isEncrypted = tokenKind === 'plain-but-encrypted';
        }

        const norm = (s) => (s || '').trim();
        const payload = {
          company: norm(company),
          email: norm(email).toLowerCase(),
          vc: norm(vc),
        };

        if (payload.company || payload.email || payload.vc) {
          await this._postContactLoginCompany(payload, {
            is_encrypted: isEncrypted,
            token_kind: tokenKind,
            raw_token: rawToken,
          });

          await this._handleMagicLink(payload);
        }
      },

      async checkPassword() {
        this.passwordError = '';
        this.passwordErrors = [];
        if (this.setPassword) {
          this.password_rules.forEach((rule) => {
            if (!rule.regex.test(this.setPassword)) this.passwordErrors.push(rule.message);
          });
          if (this.setPassword.length && this.conformPassword.length) {
            if (this.setPassword != this.conformPassword) {
              this.passwordErrors.push('Password mismatch!');
            } else if (!this.passwordErrors.length) {
              await this.$store.dispatch('applicationUsers/fetchUserEmailVerifyDetails', {
                ...(this.contactDetails &&
                  this.contactDetails._id && {
                    contact_id: this.contactDetails._id,
                  }),
                ...(this.signin && this.signin.email && { email: this.signin.email }),
                verification_code: this.otp,
                password: this.setPassword,
              });
              if (this.getUserEmailVerifyDetails) {
                this.$notify({
                  title: 'Success',
                  message: 'Password Set Successfully!',
                  type: 'success',
                });
                this.isSettedPassword = false;
                if (this.contactDetails.email) this.signin.email = this.contactDetails.email;
                if (this.getUserEmailVerifyDetails?.data)
                  this.contactDetails = this.getUserEmailVerifyDetails.data;
                if (this.selectedCompany) this.selectedCompany.password = true;
                this.otp = '';
                this.setPassword = '';
                this.conformPassword = '';
              } else {
                this.$notify.error({
                  title: 'Error',
                  message: this.getUserEmailVerificationErrors?.message || 'Error while verifying',
                });
                this.passwordErrors = [];
              }
            }
          }
        } else {
          this.passwordErrors.push('Password is required');
        }
      },
      setContactDetailsFromCompany(company) {
        this.contactDetails = {
          email: company?.email || '',
          phone: company?.phone || '',
        };
      },
      async resendVerificationCode() {
        if (!this.selectedCompany) {
          this.$notify.error({
            title: 'Error',
            message: 'Select a workspace first.',
          });
          return;
        }
        if (!this.contactDetails?.email && !this.contactDetails?.phone) {
          this.$notify.error({
            title: 'Error',
            message: 'Cannot resend code: missing user contact details.',
          });
          return;
        }
        this.submitLoading = true;
        try {
          const payload = {
            company_id: this.selectedCompany._id,
            ...(this.contactDetails.email
              ? { email: this.contactDetails.email }
              : { phone: this.contactDetails.phone }),
          };
          await this.$store.dispatch('applicationUsers/fetchForgotAndResendVerify', payload);
          if (this.getForgotAndResendVerify?.data?.status) {
            const dst = this.contactDetails.email ?? this.contactDetails.phone;

            this.$notify({
              title: 'Success',
              dangerouslyUseHTMLString: true,
              message:
                '<div style="display:inline-block;max-width:max-content;text-align:left;white-space:normal;letter-spacing:normal;word-spacing:normal;line-height:1.25;">' +
                'We\u2019ve sent an OTP to ' +
                '<strong style="white-space:nowrap;">' +
                dst +
                '</strong>.' +
                '</div>',
              type: 'success',
            });
          } else {
            this.$notify.error({
              title: 'Error',
              message: this.getForgotAndResendVerifyError?.message || 'Error resending OTP.',
            });
          }
        } catch (error) {
          this.$notify.error({
            title: 'Error',
            message: 'Something went wrong while resending OTP.',
          });
        } finally {
          this.submitLoading = false;
        }
      },

      async verifyEmail(formType, e) {
        if (e) e.preventDefault();
        this.verification.email = (this.verification.email || '').trim().toLowerCase();
        if (!this.verification.email) {
          this.getErrors = { verification_email: 'Email is required!' };
          return;
        }
        if (!this.isEmailValid) {
          this.getErrors = { verification_email: 'Enter a valid email address!' };
          return;
        }
        this.getErrors = null;
        this.$refs[formType].validate(async (valid) => {
          if (!valid) return false;
          this.submitLoading = true;

          await this.$store.dispatch('company/fetchCompaniesByEmail', {
            email: this.verification.email,
          });

          if (this.getCompaniesByEmail?.status && this.getCompaniesByEmail.companies?.length) {
            this.currentForm = 'VERIFIED';
            this.signin.email = this.getEmail;
            this.zeroCompaniesNote = '';
          } else {
            this.currentForm = 'default';
            this.zeroCompaniesNote =
              'We couldn’t find a workspace for this email. If you’re new, ask your admin to invite you or register.';
          }
          this.submitLoading = false;
        });
      },

      async submitForm(formType) {
        this.signin.email = (this.signin.email || '').trim().toLowerCase();

        if (!this.signin.email) {
          this.getErrors = { email: 'Email is required!' };
          return;
        }
        if (!this.isEmailValid) {
          this.getErrors = { email: 'Enter a valid email address!' };
          return;
        }
        if (!this.signin.workspace) {
          this.getErrors = { workspace: 'Workspace is required!' };
          return;
        }
        if (this.selectedCompany?.password && !this.signin.password) {
          this.getErrors = { password: 'Password is required!' };
          return;
        }

        this.getErrors = null;

        this.$refs[formType].validate(async (valid) => {
          if (!valid) return false;
          this.submitLoading = true;

          await this.$store.dispatch('applicationUsers/fetchSetUserSignInVerify', {
            contact_id: this.signin.workspace,
            password: this.signin.password,
          });

          if (this.getAuthenticationStatus) {
            this.$router.push({ path: '/ap/mfa', query: this.$route.query });
          } else {
            this.$notify.error({
              title: 'Error',
              message: this.getUserSignInErrors?.message || 'Error while signing in',
            });
            this.submitLoading = false;
          }
        });
      },

      goToBuilder() {
        this.$router.push({ path: '/signin' });
      },
      goToForgotPassword() {
        this.$router.push({ path: '/tenent-forgot-password' });
      },
      redirectToSetPassword() {
        this.$router.push({
          path: '/tenent-set-password',
          query: { email: this.signin.email, contact_id: this.signin.workspace },
        });
      },

      _normalizeSlugLike(s) {
        return (s || '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '');
      },
      _safeDecode(v) {
        try {
          return decodeURIComponent(v || '');
        } catch {
          return v || '';
        }
      },
      _pickWorkspaceByCompanySlug({ slugFromUrl, companyDoc }) {
        if (!Array.isArray(this.getWorkspaceList) || !this.getWorkspaceList.length) return null;
        const urlNorm = this._normalizeSlugLike(slugFromUrl);
        let match = this.getWorkspaceList.find((c) => this._normalizeSlugLike(c?.name) === urlNorm);
        if (match) return match;
        const docNameNorm = this._normalizeSlugLike(companyDoc?.name || companyDoc?.slug);
        if (docNameNorm) {
          match = this.getWorkspaceList.find(
            (c) => this._normalizeSlugLike(c?.name) === docNameNorm
          );
          if (match) return match;
        }
        return null;
      },
      _enterVerified(email) {
        this.currentForm = 'VERIFIED';
        this.signin.email = email;
        this.getErrors = null;
      },

      async _handleMagicLink(payload) {
        const rawCompany = payload.company;
        const rawEmail = payload.email;
        const rawOtp = payload.vc;

        const email = (rawEmail || '').trim().toLowerCase();
        const companySlug = this._normalizeSlugLike(rawCompany || '');
        const otp = (rawOtp || '').trim();

        if (!email || !companySlug) return;

        this.fromMagicLink = true;
        this.otp = otp || '';
        this.verification.email = email;

        try {
          await this.$store.dispatch('company/fetchCompanyBySlug', {
            slug: companySlug,
          });
        } catch (err) {
          void err;
        }

        await this.$store.dispatch('company/fetchCompaniesByEmail', { email });

        if (this.getCompaniesByEmail?.status && this.getCompaniesByEmail.companies?.length) {
          this._enterVerified(email);

          const picked = this._pickWorkspaceByCompanySlug({
            slugFromUrl: companySlug,
            companyDoc: this.getContactLoginSlug,
          });

          if (picked) {
            this.signin.workspace = picked.contact_id;
            this.selectedCompany = picked;

            await this._postContactLoginCompany(
              { company: payload.company, email: payload.email, vc: payload.vc },
              { is_encrypted: true, token_kind: 'post-select' }
            );
          } else {
            this.$notify({
              title: 'Notice',
              message: "We couldn't match the workspace from the link. Please choose it manually.",
              type: 'warning',
            });
          }
        } else {
          this.$notify.error({
            title: 'Not found',
            message: 'No workspace found for this email.',
          });
        }
      },
    },
  };
</script>

<style scoped>
  a {
    text-decoration: none !important;
    color: #196392 !important;
  }

  a:hover {
    text-decoration: underline !important;
    color: #196392 !important;
  }

  .login-form {
    max-width: 400px;
    margin: 0 auto;
  }

  .forgot-login-row {
    display: flex;
    justify-content: space-between;
  }

  .full-width {
    width: 100%;
  }

  .divider {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .divider .line {
    height: 1px;
    width: 100%;
    background-color: #ccc;
  }

  .divider-text {
    padding: 0 10px;
  }

  .password-input-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .error {
    color: red;
  }

  .forgot-login-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    flex-wrap: wrap;
  }

  .forgot-password-link {
    /* font-size: 14px;
  color: #000000; */
    text-decoration: none;
    /* font-weight: bold; */
    cursor: pointer;
    margin: 4px 0;
  }

  .forgot-password-link:hover {
    text-decoration: underline;
  }

  .password-toggle {
    cursor: pointer;
    margin-left: 8px;
    display: flex;
    align-items: center;
  }

  .password-toggle i {
    font-size: 18px;
    color: #333;
  }
  .form-item-top-label ::v-deep .el-form-item__label {
    display: block;
    margin-bottom: 4px;
    font-weight: 500;
  }

  .custom-password-input ::v-deep .el-input__inner {
    padding-right: 35px;
  }

  .toggle-icon {
    cursor: pointer;
    color: #606266;
    font-size: 18px;
  }

  .form-section-heading {
    font-size: 16px;
    font-weight: 500;
    text-decoration: underline;
    margin-bottom: 2px;
    color: #333;
  }

  .el-form-item {
    margin-bottom: 19px !important;
  }

  .el-form-item__error {
    margin-top: 5px;
    font-size: 13px;
    line-height: 1.3;
    color: #f56c6c;
  }

  .no-underline {
    text-decoration: none;
  }
</style>
